import { useState } from "react";
import { clsx } from "clsx";
import { UnitPromotions } from "@/components/units/unit/info/UnitPromotions";
import { UnitDimension } from "@/components/units/unit/info/UnitDimension";
import { UnitFeatures } from "@/components/units/unit/info/UnitFeatures";
import { UnitUrgency } from "@/components/units/unit/info/UnitUrgency";
import { UnitPrice } from "@/components/units/unit/info/UnitPrice";
import { UnitImage } from "@/components/units/unit/info/UnitImage";
import type { UnitGroup } from "@/types";
import ErrorBoundary from "@/components/ErrorBoundary";
import { useUnitDataAttributes } from "@/hooks/useUnitDataAttributes";
import { TriggerButtons } from "@/components/units/actions/TriggerButtons";
import { useOpenEsign } from "@/hooks/useOpenEsign.tsx";
import { UnitItemContext } from "@/context/UnitItemContext";

interface UnitItemProps {
    unitGroup: UnitGroup;
    layout: "grid" | "row";
}

export const UnitItem = ({ unitGroup, layout = "row" }: UnitItemProps) => {
    const { representative_unit, rates, available } = unitGroup;
    const [selectedPromo, setSelectedPromo] = useState<number>(() => {
        if (rates.promo_rates.length === 1 || rates.promo_rates.length > 1) {
            // Non-null assertion - this will never be undefined.
            return rates.promo_rates[0]!.id;
        } else {
            return 0;
        }
    });
    useOpenEsign(unitGroup, selectedPromo);
    const dataAttributes = useUnitDataAttributes(unitGroup);

    return (
        <ErrorBoundary>
            <UnitItemContext.Provider
                value={{ unitGroup, selectedPromo, setSelectedPromo }}
            >
                <div
                    className={clsx("unit-item", "unit-item--" + layout)}
                    {...dataAttributes}
                >
                    <div className="unit-item__image">
                        <UnitImage
                            category={representative_unit.category}
                            length={representative_unit.length}
                            width={representative_unit.width}
                        />
                    </div>
                    <div className="unit-item__dimension">
                        <UnitDimension unitGroup={unitGroup} />
                    </div>
                    <div className="unit-item__category">
                        {representative_unit.category?.name ?? ""}
                    </div>
                    <div className="unit-item__features">
                        <UnitFeatures features={representative_unit.features} />
                    </div>
                    <div className="unit-item__promotions">
                        <UnitPromotions
                            promotions={rates.promo_rates}
                            selectedPromo={selectedPromo}
                            onPromoChange={(promoId) =>
                                setSelectedPromo(promoId)
                            }
                        />
                    </div>
                    <div className="unit-item__urgency">
                        <UnitUrgency available={available} />
                    </div>
                    <div className="unit-item__price">
                        <UnitPrice
                            unitGroup={unitGroup}
                            selectedPromo={selectedPromo}
                        />
                    </div>
                    <div className="unit-item__action">
                        <TriggerButtons
                            unitGroup={unitGroup}
                            selectedPromo={selectedPromo}
                        />
                    </div>
                </div>
            </UnitItemContext.Provider>
        </ErrorBoundary>
    );
};
